import type {
  AssetFamilySearchFilterEnum,
  FileTypeSearchFilterEnum,
  MediaTypeSearchFilterEnum,
} from '../searchParameters';
import { AssetTypeSearchFilterEnum } from '../searchParameters';
import { ContentTypeEnum, contentTypeOf } from '../contentType';
import type { RelatedTerm, RelatedTermDto } from '../relatedTerm';
import type {
  ContentTypeNavLink,
  CuratedSet,
  EditorialEvent,
  GroupByEventCounts,
  QueryCorrections,
  SearchResults,
  SeoHeadlineSegment,
} from './searchResults';

type QueryCorrectionsDTO = {
  family?: { original?: AssetFamilySearchFilterEnum };
  language?: { original: string; language: string };
  phrase?: { original: string; corrected: string };
  asset_ids?: unknown;
} | null;

type IndexableContentTypeLinkDTO = {
  indexable: boolean;
  canonicalPhraseSlug: string | null;
};

type GsrpIndexableContentTypeLinksDTO = {
  phraseSlug: string;
  results: Partial<Record<ContentTypeEnum, IndexableContentTypeLinkDTO>>;
};

export type SearchResultsDTO = Readonly<{
  totalResults?: number;
  events?: {
    id: string;
    name?: string;
    editorial_products?: string[];
    url_slug?: string;
  }[];
  name?: string;
  counts?: {
    images?: number;
    films?: number;
    eventGroups?: { images?: number; films?: number };
  };
  seoCustomContent?: { h1?: string };
  seoHeadlineCopySegments?: SeoHeadlineSegment[];
  gsrpIndexableContentTypeLinks?: GsrpIndexableContentTypeLinksDTO | null;
  queryCorrections?: QueryCorrectionsDTO;
  assettype?: AssetTypeSearchFilterEnum;
  mediatype?: MediaTypeSearchFilterEnum;
  assetfiletype?: FileTypeSearchFilterEnum;
  phraseProcessing?: Readonly<{ naturalLanguage?: boolean }>;
  relatedTermsViewModel?: readonly RelatedTermDto[];
  similarAsset?: Readonly<{ thumbUrl?: string }> | null;
}>;

// Tab order in the nav, which is presentation rather than a domain fact.
const CONTENT_TYPE_LINK_ORDER: ContentTypeNavLink['contentType'][] = [
  ContentTypeEnum.PHOTOGRAPHY,
  ContentTypeEnum.ILLUSTRATION,
  ContentTypeEnum.FILM,
];

// A link is eligible only when the service confirms the (content type,
// phraseSlug) pair is indexable AND self-canonical — otherwise a bot would
// follow a link to a page that canonicals somewhere else.
const isEligibleContentTypeLink = (
  result: IndexableContentTypeLinkDTO | undefined,
  phraseSlug: string
): boolean =>
  !!result && result.indexable && result.canonicalPhraseSlug === phraseSlug;

const eligibleContentTypeLinks = (
  payload: GsrpIndexableContentTypeLinksDTO
): ContentTypeNavLink['contentType'][] =>
  CONTENT_TYPE_LINK_ORDER.filter((link) =>
    isEligibleContentTypeLink(payload.results[link], payload.phraseSlug)
  );

export class SearchResultsFactory {
  fromDTO = (dto: SearchResultsDTO): SearchResults => ({
    count: dto.totalResults ?? 0,
    event: this.#eventsFor(dto)[0],
    events: this.#eventsFor(dto),
    curatedSet: this.#curatedSetFor(dto),
    seoContent: this.#seoContentFor(dto),
    corrections: this.#correctionsFor(dto),
    groupByEvent: this.#groupByEventFor(dto),
    enhancedSearch: { active: !!dto.phraseProcessing?.naturalLanguage },
    oppositeAssetTypeCounts: dto.counts
      ? { image: dto.counts.images, film: dto.counts.films }
      : undefined,
    relatedTerms: this.#relatedTermsFor(dto),
    similarAsset: dto.similarAsset?.thumbUrl
      ? { thumbUrl: dto.similarAsset.thumbUrl }
      : undefined,
  });

  #relatedTermsFor({ relatedTermsViewModel }: SearchResultsDTO): RelatedTerm[] {
    return (relatedTermsViewModel ?? []).map((term) => ({
      label: term.label,
      searchParameters: { ...term.searchParameters },
      position: term.position,
    }));
  }

  #eventsFor({ events }: SearchResultsDTO): EditorialEvent[] {
    return (events ?? []).map((event) => ({
      id: event.id,
      name: event.name ?? '',
      editorialProducts: event.editorial_products,
      urlSlug: event.url_slug,
    }));
  }

  #seoContentFor(dto: SearchResultsDTO): SearchResults['seoContent'] {
    const h1 = dto.seoCustomContent?.h1;
    const headlineSegments = dto.seoHeadlineCopySegments;
    const contentTypeLinks = this.#contentTypeLinksFor(dto);
    if (
      h1 === undefined &&
      !headlineSegments?.length &&
      !contentTypeLinks?.length
    )
      return undefined;
    return { h1, headlineSegments, contentTypeLinks };
  }

  // The active type always shows as the current tab — it's a "you are here"
  // label, not a crawlable link. Cross-links to other asset types are only
  // emitted when BOTH the current page and each target are indexable and
  // self-canonical, so we never leak crawlable outbound links from a page
  // bots shouldn't follow (and thus won't crawl back from).
  #contentTypeLinksFor({
    assettype,
    mediatype,
    assetfiletype,
    gsrpIndexableContentTypeLinks,
  }: SearchResultsDTO): readonly ContentTypeNavLink[] | undefined {
    const contentType = contentTypeOf({
      assetType: assettype,
      mediaType: mediatype,
      assetFileType: assetfiletype,
    });
    // Vector is never a nav link, so a vector page gets no nav.
    const activeLink = CONTENT_TYPE_LINK_ORDER.find(
      (link) => link === contentType
    );
    if (!activeLink) return undefined;

    const activeOnly: ContentTypeNavLink[] = [
      { contentType: activeLink, isActive: true },
    ];

    if (!gsrpIndexableContentTypeLinks) return activeOnly;

    const activeIsIndexable = isEligibleContentTypeLink(
      gsrpIndexableContentTypeLinks.results[activeLink],
      gsrpIndexableContentTypeLinks.phraseSlug
    );
    if (!activeIsIndexable) return activeOnly;

    const eligibleLinks = eligibleContentTypeLinks(
      gsrpIndexableContentTypeLinks
    );

    return CONTENT_TYPE_LINK_ORDER.filter(
      (link) => link === activeLink || eligibleLinks.includes(link)
    ).map((link) => ({
      contentType: link,
      isActive: link === activeLink,
    }));
  }

  #curatedSetFor({ name, counts }: SearchResultsDTO): CuratedSet | undefined {
    return name
      ? {
          name,
          assetTypeCounts: {
            images: counts?.images ?? 0,
            films: counts?.films ?? 0,
          },
        }
      : undefined;
  }

  #correctionsFor({
    queryCorrections,
  }: SearchResultsDTO): QueryCorrections | undefined {
    if (!queryCorrections) return undefined;
    return {
      family: queryCorrections.family
        ? { original: queryCorrections.family.original }
        : undefined,
      phraseSpelling: queryCorrections.phrase
        ? {
            original: queryCorrections.phrase.original,
            corrected: queryCorrections.phrase.corrected,
          }
        : undefined,
      phraseLanguage: queryCorrections.language
        ? {
            originalPhrase: queryCorrections.language.original,
            detectedLanguage: queryCorrections.language.language,
          }
        : undefined,
      assetIdCorrected:
        Array.isArray(queryCorrections.asset_ids) &&
        queryCorrections.asset_ids.length > 0,
    };
  }

  #groupByEventFor({
    assettype,
    counts,
  }: SearchResultsDTO): GroupByEventCounts | undefined {
    const eventGroups = counts?.eventGroups;
    if (!eventGroups) return undefined;
    const keyByAssetType: Partial<
      Record<AssetTypeSearchFilterEnum, 'images' | 'films'>
    > = {
      [AssetTypeSearchFilterEnum.IMAGE]: 'images',
      [AssetTypeSearchFilterEnum.FILM]: 'films',
    };
    const key = assettype ? keyByAssetType[assettype] : undefined;
    if (!key) return undefined;
    return {
      assetCount: counts?.[key] ?? 0,
      eventCount: eventGroups[key] ?? 0,
    };
  }
}
